import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardTimeRangeDescription, normalizeDashboardTimeRange, type DashboardTimeRange } from '../lib/timeRange';
import { useCustomers } from '../context/CustomerContext';
import { useLiveData } from '../hooks/useLiveData';
import { useTriageSummary } from '../hooks/useTriageSummary';
import { calcMTTD, calcMTTR, calcNoiseRatio } from '../lib/utils';
import { IncidentsTab } from './IncidentsTab';
import { MttrWidget } from './MttrWidget';
import { KpiGrid } from './KpiGrid';
import { ChartsSection } from './ChartsSection';
import { ThreatIntelSection } from './ThreatIntelSection';
import { HuntsTab } from './HuntsTab';
import { AdvancedHuntingTab } from './AdvancedHuntingTab';
import { PhishingTab } from './PhishingTab';
import { CoverageTab } from './CoverageTab';
import { ReportsTab } from './ReportsTab';
import { DarkWebTab } from './DarkWebTab';
import { SpiderFootTab } from './SpiderFootTab';
import { DnstwistTab } from './DnstwistTab';
import { IOCUploadTab } from './IOCUploadTab';
import { Header } from './Header';
import { EnterpriseShell } from './layout/EnterpriseShell';
import { PORTAL_TABS_SPEC, TAB_CAPABILITY_MAP, type PortalTabSpec } from '../lib/permissionsCatalog';

/**
 * Stripped-down portal layout shown to users with role === 'customer_viewer'.
 *
 * Tabs exposed (per product requirement): Overview, Incidents, Hunts, Phishing,
 * Dark-Web Scan, Coverage, Threat Intelligence, Reports. The AI Assistant (Data Lake) tab is intentionally NOT exposed in the
 * portal — both /ai-chat and /kql are server-gated by `blockCustomerViewer`
 * so the entire tab would 403. Each underlying tab component derives
 * its write controls from the per-customer × per-tab matrix via
 * `useAuth().canWrite(tab, customerId)` — so an admin can grant a portal user
 * write on incidents/hunts/phishing for their scoped customer and the UI
 * exposes the buttons. The default for a customer_viewer with no matrix
 * entry stays fully read-only. Server-side, customerScopeGuard locks every
 * request to `scopedCustomerId` and `requireTabWrite` re-enforces the matrix
 * — the FE checks here are just so the user gets a visible cue instead of
 * a 403.
 */

// Portal nav comes from `PORTAL_TABS_SPEC` in `lib/permissionsCatalog.ts` —
// the single source of truth shared with the admin matrix editor so the
// two can never drift apart. `datalake` (AI Assistant) is
// intentionally absent from the spec: both /ai-chat and /kql are
// server-gated by `blockCustomerViewer`, so the tab would 403 for portal
// users.
type PortalTab = PortalTabSpec["id"];
const PORTAL_TABS = PORTAL_TABS_SPEC;

export function CustomerPortal() {
  const { user, signOut, canSee, canWrite, canCapability } = useAuth();
  const { customers, setActiveCustomerId } = useCustomers();
  const [activeTab, setActiveTab] = useState<PortalTab>('overview');
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const s = localStorage.getItem('soc-darkmode');
    return s !== null ? s === 'true' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) root.classList.add('dark'); else root.classList.remove('dark');
    localStorage.setItem('soc-darkmode', String(darkMode));
  }, [darkMode]);

  // Force the active customer to the user's scoped customer. Every nested
  // tab component reads activeCustomerId from CustomerContext, so this is
  // the single point that locks the entire portal to one tenant.
  useEffect(() => {
    if (user?.scopedCustomerId) setActiveCustomerId(user.scopedCustomerId);
  }, [user?.scopedCustomerId, setActiveCustomerId]);

  const customerId = user?.scopedCustomerId ?? '';

  // Filter PORTAL_TABS by the per-tab Visible flag in the matrix. Each
  // spec entry carries its catalog `permission` id directly, so toggling
  // Visible in the admin matrix hides the tab here too — no separate
  // NAV_TAB_TO_PERMISSION lookup needed.
  const visiblePortalTabs = PORTAL_TABS.filter((t) => {
    // Same two-gate chain the SOC dashboard uses in App.tsx: the shared
    // Permissions Master / Tab Visibility Master capability matrix gates
    // every tab, AND the legacy per-customer matrix still applies. Keeps
    // the portal consistent with how SOC L1/L2/manager nav visibility is
    // decided instead of a separate, partially-wired ruleset.
    const gate = TAB_CAPABILITY_MAP[t.id];
    if (gate && !canCapability(gate.moduleId, gate.capability)) return false;
    return canSee(t.permission, customerId);
  });

  // If the active tab gets hidden by an admin matrix change while the user is
  // viewing it, fall back to overview so we don't render a tab that's been
  // pulled out of the nav.
  useEffect(() => {
    if (!visiblePortalTabs.some((t) => t.id === activeTab)) {
      setActiveTab('overview');
    }
  }, [visiblePortalTabs, activeTab]);
  const customer = customers.find((c) => c.id === customerId);
  // Matrix-authoritative: a portal user only sees Incidents write buttons
  // (Assign / Close / Deep Investigate) when their admin has granted
  // incidents.write on this customer. Default is fully read-only.
  const incidentsReadOnly = !canWrite('incidents', customerId);
  // Top-bar date filter — drives both useLiveData and the KPI/charts block.
  // Customer portal supports the same range options as the analyst dashboard.
  const [dateFilter, setDateFilter] = useState<DashboardTimeRange>(() => {
    if (typeof window === 'undefined') return '24h';
    return normalizeDashboardTimeRange(localStorage.getItem('soc-portal-time-range'));
  });
  // Severity / status filters mirror the analyst dashboard so KPI tiles can
  // narrow the Incidents list. Clicking a KPI also jumps to the Incidents tab.
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const handleFilterSeverity = (s: string) => { setSeverityFilter(s); setActiveTab('incidents'); };
  // IncidentsTab's statusFilter is multi-select (string[]); KPI tiles only
  // ever offer a single status, so translate 'all' -> [] (no filter) and
  // anything else -> a one-element array before handing off.
  const handleFilterStatus = (s: string) => { setStatusFilter(s === 'all' ? [] : [s]); setActiveTab('incidents'); };
  useEffect(() => {
    localStorage.setItem('soc-portal-time-range', dateFilter);
  }, [dateFilter]);
  const {
    incidents, isLoading, error: liveError, lastRefresh, refresh, source, azureConfigured,
    dailyAlerts, threatArticles, secureScore,
  } = useLiveData(30, customerId, dateFilter);

  // Same derivations as the analyst Overview so KPI tiles, charts and
  // ThreatIntelSection get identical inputs.
  const triageSummary = useTriageSummary(customerId, '30d');
  const mttd = triageSummary.mttd ?? calcMTTD(incidents);
  const mttr = triageSummary.mttr ?? calcMTTR(incidents);
  const noiseRatio = calcNoiseRatio(incidents);
  const secureScoreLabel = secureScore ? `${secureScore.percentage}%` : '-';

  const highIncidents = incidents.filter((i) => i.severity === 'High').length;
  const activeIncidents = incidents.filter((i) => {
    const s = (i.status || '').toLowerCase();
    return s === 'active' || s === 'inprogress' || s === 'in progress';
  }).length;
  const newIncidents = incidents.filter((i) => (i.status || '').toLowerCase() === 'new').length;
  const resolvedIncidents = incidents.filter((i) => (i.status || '').toLowerCase() === 'closed').length;

  // Plain-English summary for the Overview banner.
  const summary = useMemo(() => {
    const open = incidents.filter((i) => (i.status || '').toLowerCase() !== 'closed');
    const critical = open.filter((i) => i.severity === 'Critical').length;
    const high = open.filter((i) => i.severity === 'High').length;
    const medium = open.filter((i) => i.severity === 'Medium').length;
    const low = open.filter((i) => i.severity === 'Low' || i.severity === 'Informational').length;
    const closedRecently = incidents.filter((i) => (i.status || '').toLowerCase() === 'closed').length;
    return { open: open.length, critical, high, medium, low, closedRecently };
  }, [incidents]);

  // Plain-English banner shown at the top of the portal Overview. We keep a
  // white card background across all severities (per customer feedback the
  // tinted banners felt heavy on a friendly portal) and use the coloured dot
  // + heading text to convey severity instead.
  const bannerBg = 'bg-white dark:bg-card border-border';
  const bannerSubtext = 'text-muted-foreground';
  const tone = summary.critical > 0
    ? { bg: bannerBg, dot: 'bg-rose-500', text: 'text-rose-700 dark:text-rose-300', subtext: bannerSubtext,
        headline: `${summary.critical} critical incident${summary.critical === 1 ? '' : 's'} open. Our SOC is responding now.` }
    : summary.high > 0
    ? { bg: bannerBg, dot: 'bg-red-500', text: 'text-red-700 dark:text-red-300', subtext: bannerSubtext,
        headline: `${summary.high} high-severity incident${summary.high === 1 ? '' : 's'} open. Our SOC is on it.` }
    : summary.open > 0
      ? { bg: bannerBg, dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-300', subtext: bannerSubtext,
          headline: `${summary.open} open incident${summary.open === 1 ? '' : 's'} under investigation. No action needed from you.` }
      : { bg: bannerBg, dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', subtext: bannerSubtext,
          headline: 'All clear. No open incidents in your environment right now.' };

  if (!user || !user.scopedCustomerId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
        <div className="text-center">
          <div className="text-lg font-bold mb-2">Portal access not configured</div>
          <p className="text-sm text-muted-foreground mb-4">Your account isn't scoped to a customer yet. Contact your SOC administrator.</p>
          <button onClick={signOut} className="text-xs px-3 py-1.5 rounded bg-primary text-white">Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <EnterpriseShell
      visibleTabs={visiblePortalTabs}
      activeTab={activeTab}
      onNavigate={(id) => setActiveTab(id as PortalTab)}
      customerName={customer?.name}
      header={
        <Header
          darkMode={darkMode}
          onToggleTheme={() => setDarkMode((d) => !d)}
          activeFilter={dateFilter}
          onFilterChange={setDateFilter}
          lastRefresh={lastRefresh}
          onRefresh={refresh}
          onOpenSettings={() => {}}
          azureConfigured={azureConfigured}
          dataSource={source}
          isLoading={isLoading}
          customers={customer ? [customer] : []}
          activeCustomerId={customerId}
          activeCustomerName={customer?.name}
          activeCustomerColor={customer?.color}
          hideSettings
          subtitle="Your security dashboard, managed by your SOC team."
        />
      }
    >
        {liveError && (
          <div className="mb-3 px-4 py-2 bg-yellow-500/20 border border-yellow-500/40 rounded-lg text-yellow-800 dark:text-yellow-200 text-xs">
            {liveError}
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="slide-in space-y-4">
            {/* Plain-English status banner is portal-specific — kept at the
                top so a non-technical reader sees the headline before the
                analyst-style charts. */}
            <div className={`px-4 py-3 rounded-lg border flex items-start gap-3 ${tone.bg}`}>
              <span className={`mt-1 inline-block w-2.5 h-2.5 rounded-full ${tone.dot} flex-shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold ${tone.text}`}>{tone.headline}</div>
                <div className={`text-[11px] mt-0.5 ${tone.subtext}`}>
                  {dashboardTimeRangeDescription(dateFilter)} · {summary.open} open ({summary.high} high, {summary.medium} medium, {summary.low} low)
                  {summary.closedRecently > 0 && ` · ${summary.closedRecently} resolved`}
                </div>
              </div>
            </div>

            {/* Same KPI / charts / threat-intel block as the SOC L1 Overview. */}
            <KpiGrid
              secureScore={secureScoreLabel}
              highIncidents={highIncidents}
              totalIncidents={incidents.length}
              activeIncidents={activeIncidents + newIncidents}
              resolvedIncidents={resolvedIncidents}
              onFilterSeverity={handleFilterSeverity}
              onFilterStatus={handleFilterStatus}
              dateFilter={dateFilter}
            />
            <ChartsSection dailyAlerts={dailyAlerts} incidents={incidents} darkMode={darkMode} />
            <div className="mb-4">
              <MttrWidget customerId={customerId} defaultRange="30d" />
            </div>
            <ThreatIntelSection
              articles={threatArticles}
              mttd={mttd}
              mttr={mttr}
              noiseRatio={noiseRatio}
              incidents={incidents}
              customerId={customerId}
            />
            <IncidentsTab
              incidents={incidents.slice(0, 10)}
              severityFilter={severityFilter}
              statusFilter={statusFilter}
              onSeverityFilter={handleFilterSeverity}
              onStatusFilter={setStatusFilter}
              onIncidentAction={() => {}}
              customerId={customerId}
              readOnly={incidentsReadOnly}
            />
          </div>
        )}

        {activeTab === 'incidents' && (
          <IncidentsTab
            incidents={incidents}
            severityFilter={severityFilter}
            statusFilter={statusFilter}
            onSeverityFilter={setSeverityFilter}
            onStatusFilter={setStatusFilter}
            onIncidentAction={() => {}}
            customerId={customerId}
            readOnly={incidentsReadOnly}
          />
        )}

        {activeTab === 'hunts'    && <HuntsTab />}
        {activeTab === 'advanced_hunting' && <AdvancedHuntingTab fixedCustomerId={customerId} hideCustomerSelector />}
        {activeTab === 'phishing' && <PhishingTab />}
        {activeTab === 'darkweb'  && <DarkWebTab />}
        {activeTab === 'spiderfoot' && <SpiderFootTab fixedCustomerId={customerId} hideCustomerSelector />}
        {activeTab === 'dnstwist' && <DnstwistTab fixedCustomerId={customerId} hideCustomerSelector />}
        {activeTab === 'coverage' && <CoverageTab />}
        {activeTab === 'ioc'      && <IOCUploadTab />}
        {activeTab === 'reports'  && <ReportsTab />}
    </EnterpriseShell>
  );
}
